import { useEffect, useRef, useState } from "react";
import { Button, Form, Image, Input, Select, Space, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Api from "../../../../api/Api";
import FileApi from "../../../../api/FileApi";
import SceneApi from "../../../../api/SceneApi";
import SessionService from "../../../../services/session/SessionService";
import MapPicker from "../../../../components/ui-components/map-picker/MapPicker";
import { CreateDeviceTemplateWizardWrapper, FormWrapper, MapPickerWrapper, OperationWrapper, Title, TitleWrapper } from "./CreateSceneWizard.styled";

interface CreateSceneWizardProps {
    onClose: ()=> void,
    onComplete: ()=> void,
    scene?: any,
}

export const CreateSceneWizard: React.FC<CreateSceneWizardProps> = (props)=> {
    // api
    let sceneApi = new SceneApi();
    const [form] = Form.useForm();

    let defaultSceneParams = {} as any;
    if (props.scene) {
        defaultSceneParams = {
            id: props.scene.id,
            name: props.scene.name,
            thumbnail: props.scene.thumbnail,
            geoLongitude: props.scene.geoLongitude,
            geoLatitude: props.scene.geoLatitude,
            remarks: props.scene.remarks,
        };
    }

    // state
    let [submitEnabled, setSubmitEnabled] = useState(false);
    let [createSceneParams, setCreateSceneParams] = useState(defaultSceneParams);
    let [openPreview, setOpenPreview] = useState(false);
    let [thumbnailUploadFileList, setThumbnailUploadFileList] = useState<any[]>(props.scene ? [{
            uid: '-1',
            name: 'thumbnail.png',
            status: 'done',
            url: Api.URL_PREFIX + props.scene.thumbnail + "?Authorization=" + SessionService.getInstance().getAuthoriazation(),
        },] : []);

    // ref
    let createSceneParamsRef = useRef(createSceneParams);
    let aMapTypeRef = useRef<any>(null);
    let aMapRef = useRef<any>(null);

    // validate
    let validateForm = ()=> {
        console.log(createSceneParams);
        createSceneParamsRef.current = {...createSceneParams};
        setCreateSceneParams({...createSceneParams});
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            console.log(err);
        });
    }

    // events
    let onNameChange = (e)=> {
        createSceneParams.name = e.target.value;
        validateForm();
    };

    let onAddressChange = (e)=> {
        createSceneParams = createSceneParamsRef.current;
        let address = e.target.value;
        createSceneParams.address = address;
        validateForm();

        let AMap = aMapTypeRef.current;
        AMap.plugin('AMap.Geocoder', function() {
            var geocoder = new AMap.Geocoder({});
            geocoder.getLocation(address, function(status, result) {
                if (status === 'complete' && result.info === 'OK') {
                    if (result.geocodes && result.geocodes.length > 0) {
                        var location = result.geocodes[0].location;
                        createSceneParams.geoLatitude = location.lat;
                        createSceneParams.geoLongitude = location.lng;
                        createSceneParams.lnglat = createSceneParams.geoLongitude + ', ' + createSceneParams.geoLatitude;
                        form.setFieldValue('lnglat', createSceneParams.lnglat);
                        validateForm();
                        aMapRef.current.zoomTo(location.lng, location.lat);
                    }
                } else {
                    console.error("地理编码失败:", result.info);
                }
            });
        });
    };

    let onPreview = (file)=> {
        setOpenPreview(true);
    };

    let onRemove = (file)=> {
        thumbnailUploadFileList = [];
        setThumbnailUploadFileList([]);
        validateForm();
        return true;
    };

    let onUpload = (file: any)=> {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            let base64DataUrl = reader.result as string;
            createSceneParams.thumbnail = base64DataUrl;
            let thumbnailUploadFile = {
                uid: file.uid,
                name: file.name,
                status: 'done',
                url: base64DataUrl,
            };
            thumbnailUploadFileList = [thumbnailUploadFile];
            setThumbnailUploadFileList(thumbnailUploadFileList);
            form.setFieldValue('thumbnail', base64DataUrl)
            validateForm();
        }
        return false;
    };

    let onMapInit = (AMap, aMap)=> {
        aMapTypeRef.current = AMap;
        aMapRef.current = aMap;
    }

    let onPick = (lng, lat)=> {
        createSceneParams = createSceneParamsRef.current;
        createSceneParams.geoLatitude = lat;
        createSceneParams.geoLongitude = lng;
        createSceneParams.lnglat = createSceneParams.geoLongitude ? createSceneParams.geoLongitude + ', ' + createSceneParams.geoLatitude : '';
        form.setFieldValue('lnglat', createSceneParams.lnglat);
        validateForm();

        let AMap = aMapTypeRef.current;
        AMap.plugin('AMap.Geocoder', function() {
            let geocoder = new AMap.Geocoder({});
            geocoder.getAddress([lng, lat], function(status, result) {
                if (status === 'complete' && result.info === 'OK') {
                    console.log("格式化地址:", result.regeocode.formattedAddress);
                    createSceneParams.address = result.regeocode.formattedAddress;
                    form.setFieldValue('address', createSceneParams.address);
                    validateForm();
                } else {
                    console.error("逆地理编码失败:", result.info);
                }
            });
        });
    };

    let onRemarksChange = (e)=> {
        createSceneParams.remarks = e.target.value;
        validateForm();
    };

    let onSubmit = async (e)=> {
        if (createSceneParams.id) {
            await sceneApi.update(createSceneParams);
        } else {
            await sceneApi.create(createSceneParams);
        }
        props.onComplete();
        props.onClose();
    };

    let onCancel = (e)=> {
        props.onClose();
    };

    // effect
    useEffect(()=> {
        validateForm();
    }, []);

    // render
    return (
        <CreateDeviceTemplateWizardWrapper>
            <TitleWrapper>
                <Title>创建场景</Title>
            </TitleWrapper>

            <FormWrapper>
                <Form form={form} name="form" layout="vertical">
                    <Form.Item name="thumbnail" label="场景图片" rules={[{required: true}]} style={{justifyContent: 'center'}}>
                        <Upload
                            action={FileApi.UPLOAD_URL}
                            listType="picture-circle"
                            fileList={thumbnailUploadFileList}
                            onPreview={onPreview}
                            onRemove={onRemove}
                            beforeUpload={onUpload}
                        >
                            {
                                thumbnailUploadFileList.length <=0 && 
                                <button style={{ border: 0, background: 'none' }} type="button">
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </button>
                            }
                        </Upload>

                        <Image
                            wrapperStyle={{ display: 'none' }}
                            src={thumbnailUploadFileList[0]?.url}
                            preview={{
                                visible: openPreview,
                                onVisibleChange: (visible) => setOpenPreview(visible),
                                afterOpenChange: (visible) => !visible && setOpenPreview(false),
                            }}
                        />
                    </Form.Item>
                
                    <Form.Item name="name" label="场景名称" rules={[{required: true}]} initialValue={createSceneParams.name}>
                        <Input onChange={onNameChange} />
                    </Form.Item>

                    { /* <Form.Item name="address" label="地址" rules={[{required: false}]} initialValue={createSceneParams.address} style={{flex: '0.5 0.5 25%'}}>
                        <Input onChange={onAddressChange} />
                    </Form.Item>

                    <Form.Item name="lnglat" label="经纬度" rules={[{required: false}]} style={{flex: '0.5 0.5 25%'}}>
                        <Input disabled={true} />
                    </Form.Item>

                    <MapPickerWrapper>
                        <MapPicker lng={createSceneParams.geoLongitude} lat={createSceneParams.geoLatitude} onPick={onPick} onMapInit={onMapInit}/>
                        </MapPickerWrapper> */ }

                    <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={createSceneParams.remarks} style={{paddingTop: '10px'}}>
                        <Input.TextArea style={{height: '75px'}} onChange={onRemarksChange}/>
                    </Form.Item>
                </Form>
            </FormWrapper>

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </CreateDeviceTemplateWizardWrapper>
    )
}
